/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/array-type */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {TF} from '../../constants/Common';
import {SampleExcel} from '../../constants/SampleExcel';
import {ISelect} from '../../interface/ISelect';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import supplierInfo from '../../services/modules/OM/supplierInfoDetails';
import B2bApi from '../../services/modules/PRODUCTS/B2bAPI';
import productInfoAPI from '../../services/modules/PRODUCTS/ProductInfoAPI';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import DisplayModal from '../DisplayModal/DisplayModal';
import B2bDis from '../DisplayModal/ProductInfoDis/B2bDis';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import B2bForm from '../Forms/B2bForm';
import {yearKeyLabel} from '../Forms/Constants/IP/commonKeyLabel';
import {FacilityKey} from '../Forms/Constants/IP/FacilityKeyLabel';
import {upstreamKey} from '../Forms/Constants/IP/UpstreamKeyLabel';
import {B2bKey, B2bLabel} from '../Forms/Constants/Products/B2BKeyLabel';
import {
  RawMaterialKey,
  RawMaterialLabel
} from '../Forms/Constants/Products/RawMaterialKeyAndLabel';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {FullInfo, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import {returnProductFilter} from './Utils/returnProductFilters';
import sumValue from './Utils/sumValue';
import TableTitleHeader from './Utils/TableTitleHeader';
import TimeFrameDataTooltip from './Utils/TimeFrameDataTooltip';

const B2BTable: React.FC<{
  selectedYear: string;
  isAverage?: 'Average' | 'Supplier';
}> = ({selectedYear, isAverage = 'Average'}) => {
  interface DataType {
    _id: string;
    fuelType: number;
    noOfUnits: boolean;
    deviceType: number;
    fuelUsed: boolean;
    method: number;
    impacts: boolean;
    owned: boolean;
    quantity: number;
    cost: number;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [showModelInfo, setShowModelInfo] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [openDisplayModal, setOpenDisplayModal] = useState(false);
  const [openDisplayModalData, setOpenDisplayModalData] = useState({});
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [formSuppliers, setFormSuppliers] = useState([]);
  const [openDisplayModalType, setOpenDisplayModalType] = useState({
    title: '',
    dataKey: ''
  });
  const companyId = useCompanyStore.getState().companyId;

  const {products} = useJSONStore.getState();
  const [rawMaterialFilter, setRawMaterialFilter] = useState<any>({});
  const [formSelectedYear, setFormSelectedYear] = useState('');
  const [formRawMaterialFilter, setFormRawMaterialFilter] = useState<any>({});

  const facilityDetails = useMemo(() => {
    return (
      useCompanyStore
        .getState()
        .facilityDetails?.[selectedYear ?? '']?.map((item: any) => {
          return {value: item._id, label: item?.[FacilityKey.FACILITY_NAME]};
        }) ?? []
    );
  }, [selectedYear]);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      B2bApi(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}/${isAverage}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  useEffect(() => {
    setIsRefresh(true);
    Promise.all([])
      .catch(() => {})
      .finally(() => {
        setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (selectedYear && allJSON) {
      setIsRefresh(true);
      Promise.all([
        B2bApi(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}/${isAverage}`
        ),
        productInfoAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}`
        ),
        isAverage === 'Supplier' &&
          supplierInfo(
            APITypeEnum.GET_ALL,
            'company',
            `${companyId}/${selectedYear}/downstream`
          )
      ])
        .then(([res1, res2, res3]) => {
          setDataSource(res1 ?? []);
          setSuppliers(
            res3.length > 0
              ? res3.map((item: any) => {
                  return {
                    label: item?.[upstreamKey.SUPPLIER_NAME],
                    value: item._id,
                    data: item?.data
                  };
                })
              : []
          );
          setRawMaterialFilter(
            returnProductFilter(
              [
                {
                  mainKey: RawMaterialKey.PRODUCT_ID,
                  filterer: products,
                  subKey: '',
                  returnFilterKey: 'productFilter'
                },
                {
                  mainKey: RawMaterialKey.FACILITY_MAIN,
                  filterer: facilityDetails,
                  subKey: RawMaterialKey.FACILITY_ID,
                  returnFilterKey: 'facilityFilter'
                }
              ],
              res2,
              {filterKey: B2bKey.PRODUCT_TYPE, filterValue: 'b2b'}
            )
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [selectedYear, allJSON]);

  useEffect(() => {
    if (formSelectedYear) {
      Promise.all([
        productInfoAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${formSelectedYear}`
        ),
        isAverage === 'Supplier' &&
          supplierInfo(
            APITypeEnum.GET_ALL,
            'company',
            `${companyId}/${formSelectedYear}/downstream`
          )
      ])
        .then(([res2, res3]) => {
          setFormSuppliers(
            res3
              ? res3.map((item: any) => {
                  return {
                    label: item?.[upstreamKey.SUPPLIER_NAME],
                    value: item._id,
                    data: item?.data
                  };
                })
              : []
          );
          const facilitDetail =
            useCompanyStore
              .getState()
              .facilityDetails?.[formSelectedYear ?? '']?.map((item: any) => {
                return {
                  value: item._id,
                  label: item?.[FacilityKey.FACILITY_NAME]
                };
              }) ?? [];
          setFormRawMaterialFilter(
            returnProductFilter(
              [
                {
                  mainKey: RawMaterialKey.PRODUCT_ID,
                  filterer: products,
                  subKey: '',
                  returnFilterKey: 'productFilter'
                },
                {
                  mainKey: RawMaterialKey.FACILITY_MAIN,
                  filterer: facilitDetail,
                  subKey: RawMaterialKey.FACILITY_ID,
                  returnFilterKey: 'facilityFilter'
                }
              ],
              res2,
              {filterKey: B2bKey.PRODUCT_TYPE, filterValue: 'b2b'}
            )
          );
        })
        .catch(() => {})
        .finally(() => {
          setIsFormLoading(false);
        });
    }
  }, [formSelectedYear]);

  const handleDelete = (_id: string[] | string) => {
    B2bApi(APITypeEnum.DELETE_ALL, {
      processingProducts: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const {productFilter, facilityFilter} = rawMaterialFilter;

  const processingB2BKeyLabel = useMemo(() => {
    return [
      {
        key: yearKeyLabel.key,
        label: yearKeyLabel.label,
        type: objTypes.string
      },
      {
        key: B2bKey.PRODUCT_ID,
        label: B2bLabel.PRODUCT_ID,
        type: objTypes.json,
        format: formSelectedYear
          ? formRawMaterialFilter?.productFilter
          : rawMaterialFilter?.productFilter
      },
      {
        key: B2bKey.FACILITY,
        label: B2bLabel.FACILITY_ID,
        type: objTypes.json,
        format: formSelectedYear
          ? formRawMaterialFilter?.facilityFilter
          : rawMaterialFilter?.facilityFilter
      },
      {
        key: B2bKey.ADD_SUPPLIER,
        label: B2bLabel.ADD_SUPPLIER,
        type: objTypes.supplierData,
        format: formSelectedYear ? formSuppliers : suppliers,
        isGreen: true,
        renderType: 'multi',
        hidden: isAverage === 'Average'
      },
      {
        key: TF,
        label: [B2bLabel.NO_UNIT_TF],
        type: objTypes.timeFrameData,
        renderType: 'multi'
      },
      ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              key: checkerStatusKey.STATUS,
              label: checkerStatusLabel.STATUS,
              type: objTypes.status
            },
            {
              key: checkerStatusKey.COMMENT,
              label: checkerStatusLabel.COMMENT,
              type: objTypes.comment
            }
          ]
        : [])
    ];
  }, [formRawMaterialFilter, rawMaterialFilter, suppliers, formSuppliers]);

  const columns: (defaultCol & ColumnsType<DataType>[0])[] =
    isAverage === 'Average'
      ? [
          {
            title: B2bLabel.PRODUCT_ID,
            dataIndex: B2bKey.PRODUCT_ID,
            editable: true,
            type: 'select',
            ...SearchFilter(
              B2bLabel.PRODUCT_ID,
              B2bKey.PRODUCT_ID,
              productFilter
            ),
            render: (value) => {
              return (
                <>
                  {
                    productFilter?.find((item: any) => {
                      return item.value === value;
                    })?.label
                  }
                </>
              );
            }
          },
          {
            title: B2bLabel.FACILITY_ID,
            dataIndex: B2bKey.FACILITY,
            editable: true,
            type: 'select',
            ...SearchFilter(
              B2bLabel.FACILITY_ID,
              B2bKey.FACILITY,
              facilityFilter
            ),
            render: (value) => {
              return (
                <>
                  {
                    facilityFilter?.find((item: any) => {
                      return item.value === value;
                    })?.label
                  }
                </>
              );
            }
          },
          {
            title: B2bLabel.NO_UNIT_TF,
            dataIndex: TF,
            editable: true,
            type: 'select',
            // render: (value) => {
            //   return (
            //     <TimeFrameRender value={value} dataKey={WasteKey.wasteProduced} />
            //   );
            // }
            render: (value) => {
              const mainData = value?.find((item: any) => {
                return item.type === B2bKey.NO_UNIT_TF;
              });

              return (
                <div
                  onClick={() => {
                    setOpenDisplayModal(true);
                    setOpenDisplayModalData(mainData);
                    setOpenDisplayModalType({
                      title: `${B2bLabel.NO_UNIT_TF} (${mainData?.unit ?? ''})`,
                      dataKey: B2bKey.NO_UNIT_TF
                    });
                  }}
                  style={{
                    textDecorationLine: 'underline',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                  <div>{`${String(formatIndian(sumValue(mainData?.data)))} ${
                    mainData?.unit ?? ''
                  }`}</div>
                </div>
              );
            }
          },
          ...(hasUserRole(UserRole.Maker_Role) ||
          hasUserRole(UserRole.Both_role) ||
          hasViewRole(UserRole.View_Only)
            ? [
                {
                  title: RawMaterialLabel.STATUS_COMMENTS,
                  dataIndex: 'status',
                  editable: true,
                  type: 'select',
                  render: (value: any, record: any) => {
                    const colorText = getStatusColor(value);

                    return (
                      <div
                        style={{
                          fontWeight: 700,
                          color: colorText.color,
                          cursor: 'pointer'
                        }}
                      >
                        {colorText.text}
                        {value === 'rejected' && record?.comment && (
                          <span className="customComment">
                            - {record.comment}
                          </span>
                        )}
                      </div>
                    );
                  }
                }
              ]
            : []),
          // ...(hasUserRole(UserRole.Checker_Role) ||
          // hasUserRole(UserRole.Both_role)
          //   ? [
          //       {
          //         title: checkerStatusLabel.DATA_SOURCE,
          //         dataIndex: checkerStatusKey.DATA_SOURCE,
          //         editable: true,
          //         type: 'select',
          //         render: (value: any) => {
          //           return (
          //             <div>
          //               {value === checkerStatusKey.MANUAL
          //                 ? checkerStatusLabel.MANUAL
          //                 : checkerStatusLabel.BULK_UPLOAD}
          //             </div>
          //           );
          //         }
          //       }
          //     ]
          //   : []),
          ...(hasUserRole(UserRole.Checker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: RawMaterialLabel.STATUS,
                  dataIndex: 'status',
                  render: (value: any, record: any) => {
                    const colorText = getStatusColor(value);

                    return (
                      <Tag
                        onClick={() => {
                          setShowModelInfo(true);
                          setEditData(record);
                          setIsEdit(true);
                        }}
                        color={colorText.color}
                        style={{
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        {colorText.text}
                      </Tag>
                    );
                  }
                }
              ]
            : []),
          ...(hasUserRole(UserRole.Checker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: FullInfo.title,
                  dataIndex: 'fullInfo',
                  editable: true,
                  type: 'select',
                  render: (_: any, record: any) => {
                    return (
                      <Button
                        onClick={() => {
                          setShowModelInfo(true);
                          setEditData(record);
                          setIsEdit(true);
                        }}
                        shape="circle"
                        style={{
                          marginRight: 16,
                          cursor: 'pointer',
                          color: '#435B37',
                          background: '#F5FBF3',
                          border: 0
                        }}
                      >
                        <ExclamationOutlined />
                      </Button>
                    );
                  }
                }
              ]
            : []),
          ...(hasUserRole(UserRole.Maker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: Title.actionTitle,
                  dataIndex: 'operation',
                  type: '' as const,
                  fixed: 'right' as const,
                  width: '200px',
                  render: (value: any, record: any) =>
                    dataSource.length >= 1 ? (
                      <ActionsButton
                        record={record}
                        setShowModel={setShowModel}
                        setEditData={setEditData}
                        setIsEdit={setIsEdit}
                        handleDelete={handleDelete}
                      />
                    ) : null
                }
              ]
            : [])
        ]
      : [
          {
            title: B2bLabel.PRODUCT_ID,
            dataIndex: RawMaterialKey.PRODUCT_ID,
            editable: true,
            type: 'select',
            ...SearchFilter(
              B2bLabel.PRODUCT_ID,
              RawMaterialKey.PRODUCT_ID,
              productFilter
            ),
            render: (value) => {
              return (
                <>
                  {
                    productFilter?.find((item: any) => {
                      return item.value === value;
                    })?.label
                  }
                </>
              );
            }
          },
          {
            title: B2bLabel.FACILITY_ID,
            dataIndex: B2bKey.FACILITY,
            editable: true,
            type: 'select',
            ...SearchFilter(
              B2bLabel.FACILITY_ID,
              B2bKey.FACILITY,
              facilityFilter
            ),
            render: (value) => {
              return (
                <>
                  {
                    facilityFilter?.find((item: any) => {
                      return item.value === value;
                    })?.label
                  }
                </>
              );
            }
          },
          {
            title: B2bLabel.NO_UNIT_TF,
            dataIndex: TF,
            editable: true,
            type: 'select',
            render: (value) => {
              const mainData = value?.find((item: any) => {
                return item.type === B2bKey.NO_UNIT_TF;
              });

              return (
                <div
                  onClick={() => {
                    setOpenDisplayModal(true);
                    setOpenDisplayModalData(mainData);
                    setOpenDisplayModalType({
                      title: `${B2bLabel.NO_UNIT_TF} (${mainData?.unit ?? ''})`,
                      dataKey: B2bKey.NO_UNIT_TF
                    });
                  }}
                  style={{
                    textDecorationLine: 'underline',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                  <div>{`${String(formatIndian(sumValue(mainData?.data)))} ${
                    mainData?.unit ?? ''
                  }`}</div>
                </div>
              );
            }
          },
          {
            title: B2bLabel.ADD_SUPPLIER,
            dataIndex: B2bKey.ADD_SUPPLIER,
            editable: true,
            render: (value) => {
              return (
                <div
                  onClick={() => {
                    setOpenDisplayModal(true);
                    setOpenDisplayModalData(value);
                    setOpenDisplayModalType({
                      title: B2bLabel.ADD_SUPPLIER,
                      dataKey: RawMaterialKey.ADD_SUPPLIER
                    });
                  }}
                  style={{
                    textDecorationLine: 'underline',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                  <div>{value?.length}</div>
                </div>
              );
            }
          },
          ...(hasUserRole(UserRole.Maker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: RawMaterialLabel.STATUS_COMMENTS,
                  dataIndex: 'status',
                  editable: true,
                  type: 'select',
                  render: (value: any, record: any) => {
                    const colorText = getStatusColor(value);

                    return (
                      <div
                        style={{
                          fontWeight: 700,
                          color: colorText.color,
                          cursor: 'pointer'
                        }}
                      >
                        {colorText.text}
                        {value === 'rejected' && record?.comment && (
                          <span className="customComment">
                            - {record.comment}
                          </span>
                        )}
                      </div>
                    );
                  }
                }
              ]
            : []),
          // ...(hasUserRole(UserRole.Checker_Role) ||
          // hasUserRole(UserRole.Both_role)
          //   ? [
          //       {
          //         title: checkerStatusLabel.DATA_SOURCE,
          //         dataIndex: checkerStatusKey.DATA_SOURCE,
          //         editable: true,
          //         type: 'select',
          //         render: (value: any) => {
          //           return (
          //             <div>
          //               {value === checkerStatusKey.MANUAL
          //                 ? checkerStatusLabel.MANUAL
          //                 : checkerStatusLabel.BULK_UPLOAD}
          //             </div>
          //           );
          //         }
          //       }
          //     ]
          //   : []),
          ...(hasUserRole(UserRole.Checker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: RawMaterialLabel.STATUS,
                  dataIndex: 'status',
                  render: (value: any, record: any) => {
                    const colorText = getStatusColor(value);

                    return (
                      <Tag
                        onClick={() => {
                          setShowModelInfo(true);
                          setEditData(record);
                          setIsEdit(true);
                        }}
                        color={colorText.color}
                        style={{
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        {colorText.text}
                      </Tag>
                    );
                  }
                }
              ]
            : []),
          ...(hasUserRole(UserRole.Checker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: FullInfo.title,
                  dataIndex: 'fullInfo',
                  editable: true,
                  type: 'select',
                  render: (_: any, record: any) => {
                    return (
                      <Button
                        onClick={() => {
                          setShowModelInfo(true);
                          setEditData(record);
                          setIsEdit(true);
                        }}
                        shape="circle"
                        style={{
                          marginRight: 16,
                          cursor: 'pointer',
                          color: '#435B37',
                          background: '#F5FBF3',
                          border: 0
                        }}
                      >
                        <ExclamationOutlined />
                      </Button>
                    );
                  }
                }
              ]
            : []),
          ...(hasUserRole(UserRole.Maker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: Title.actionTitle,
                  dataIndex: 'operation',
                  type: '' as const,
                  fixed: 'right' as const,
                  width: '200px',
                  render: (value: any, record: any) =>
                    dataSource.length >= 1 ? (
                      <ActionsButton
                        record={record}
                        setShowModel={setShowModel}
                        setEditData={setEditData}
                        setIsEdit={setIsEdit}
                        handleDelete={handleDelete}
                      />
                    ) : null
                }
              ]
            : [])
        ];

  return (
    <div>
      <TableTitleHeader
        title="Sold Products Info"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Sold Products'
        }}
        sampleDownload={{href: SampleExcel.waste}}
        jobs={{setShowJobs, uploadType: uploadTypes.Waste}}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        showAddOnly={true}
        onlyYear={true}
        shouldEdit={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        }
      />

      <JobsModal
        open={showJobs}
        onCancel={() => {
          setShowJobs(false);
          setRefresh(true);
        }}
      />

      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? {
                type: 'checkbox',
                getCheckboxProps: (record: any) => ({
                  disabled: record.status === statusEm.ACCEPTED
                }),
                selectedRowKeys,
                onChange: (
                  selectedKey: React.Key[],
                  selectedRow: DataType[]
                ) => {
                  setSelectedRowKeys(
                    selectedRow.map((item) => {
                      return item._id;
                    })
                  );
                }
              }
            : undefined
        }
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />
      <DrawerForm
        open={showModel}
        onCancel={() => {
          setShowModel(false);
          setFormSelectedYear('');
        }}
        setRefresh={setRefresh}
        API={B2bApi}
        data={isEdit ? editData : undefined}
        FormComponent={
          <B2bForm
            data={isEdit ? editData : undefined}
            setSubmitDisabled={setSubmitDisabled}
            isAverage={isAverage === 'Average'}
            rawMaterialFilter={
              formSelectedYear ? formRawMaterialFilter : rawMaterialFilter
            }
            suppliers={formSelectedYear ? formSuppliers : suppliers}
            setFormSelectedYear={setFormSelectedYear}
            setIsFormLoading={setIsFormLoading}
            isFormLoading={isFormLoading}
          />
        }
        type={{key: 'method', value: isAverage}}
        idKey={`${companyId}/${selectedYear}`}
        table={true}
        buttonDisabled={{value: submitDisabled, setter: setSubmitDisabled}}
        onlyYear={true}
        stepsTitle={['Sold Product Details', 'Quantity Details']}
        formPreview={processingB2BKeyLabel}
      />
      <DisplayModal
        title={openDisplayModalType.title}
        open={openDisplayModal}
        onCancel={() => {
          setOpenDisplayModal(false);
        }}
        displayComponent={
          openDisplayModalType.title === B2bLabel.ADD_SUPPLIER ? (
            <B2bDis dataSource={openDisplayModalData} suppliers={suppliers} />
          ) : (
            <TimeFrameDataTooltip mainData={openDisplayModalData} />
          )
        }
        isTable={openDisplayModalType.title === B2bLabel.ADD_SUPPLIER}
      />
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        API={B2bApi}
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo
            formData={editData}
            formPreview={processingB2BKeyLabel}
          />
        }
        table={true}
        stepsTitle={['All Data']}
        isChecker={true}
        isAction={false}
      />
    </div>
  );
};

export default B2BTable;
